'use client'

import { useState, useEffect, Suspense, useCallback, useRef } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import Script from 'next/script'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Eye, EyeOff, LogIn } from 'lucide-react'

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
})

const TURNSTILE_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY

function LoginForm() {
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [turnstileToken, setTurnstileToken] = useState(null)
  const [turnstileReady, setTurnstileReady] = useState(!TURNSTILE_SITE_KEY) // Ready if no key configured
  const turnstileWidgetId = useRef(null)
  const router = useRouter()
  const searchParams = useSearchParams()

  useEffect(() => {
    // Check for error messages from middleware redirects
    const errorParam = searchParams.get('error')
    switch (errorParam) {
      case 'insufficient_permissions':
        setError('You do not have permission to access that page')
        break
      case 'admin_access_denied':
        setError('Admin access requires a SAAS super admin account')
        break
      case 'tenant_access_denied':
        setError('Tenant access requires a tenant admin account')
        break
      case 'insufficient_tenant_permissions':
        setError('Insufficient tenant permissions - requires admin role')
        break
      case 'invalid_token':
        setError('Your session has expired, please login again')
        break
    }
  }, [searchParams])

  // Initialize Turnstile widget
  const initTurnstile = useCallback(() => {
    if (!TURNSTILE_SITE_KEY || !window.turnstile) return

    // Remove existing widget if any
    if (turnstileWidgetId.current !== null) {
      try {
        window.turnstile.remove(turnstileWidgetId.current)
      } catch (e) {
        // Widget might already be removed
      }
    }

    const widgetId = window.turnstile.render('#turnstile-container', {
      sitekey: TURNSTILE_SITE_KEY,
      callback: (token) => {
        setTurnstileToken(token)
        setTurnstileReady(true)
      },
      'expired-callback': () => {
        setTurnstileToken(null)
        setTurnstileReady(false)
        // Re-render widget to get a new token
        if (window.turnstile && turnstileWidgetId.current !== null) {
          window.turnstile.reset(turnstileWidgetId.current)
        }
      },
      'error-callback': () => {
        setTurnstileToken(null)
        setTurnstileReady(false)
        setError('Security check failed to load. Please refresh the page and try again.')
        console.warn('Turnstile widget error')
      },
      theme: 'light',
      size: 'flexible',
    })

    turnstileWidgetId.current = widgetId
    // Don't set turnstileReady here - wait for callback to confirm token is ready
  }, [])

  // Reset Turnstile widget (call after failed login)
  const resetTurnstile = useCallback(() => {
    setTurnstileToken(null)
    setTurnstileReady(false)
    if (window.turnstile && turnstileWidgetId.current !== null) {
      try {
        window.turnstile.reset(turnstileWidgetId.current)
      } catch (e) {
        // Widget might not exist
      }
    }
  }, [])

  const form = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  })

  const onSubmit = async (data) => {
    setIsLoading(true)
    setError('')

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...data,
          turnstileToken,
        }),
      })

      const result = await response.json()

      if (!response.ok) {
        resetTurnstile()
        throw new Error(result.message || 'Login failed')
      }

      // Token is automatically stored in httpOnly cookie by server

      // Check for redirect parameter
      const redirectTo = searchParams.get('redirect')

      // This login page is for platform admins only
      if (result.user.userType === 'tenant') {
        // Tenant users should use their tenant-specific login
        setError(`Please use your tenant login page: /tenant/${result.user.tenantCode}/login`)
        return
      }

      if (redirectTo) {
        // Redirect to the originally requested page
        router.push(redirectTo)
      } else {
        // Default redirect based on user role
        if (result.user.saasRole === 'MASTER_ADMIN' || result.user.saasRole === 'SUPER_ADMIN') {
          router.push('/admin')
        } else {
          router.push('/dashboard')
        }
      }
    } catch (error) {
      setError(error.message)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
      {TURNSTILE_SITE_KEY && (
        <Script
          src="https://challenges.cloudflare.com/turnstile/v0/api.js"
          strategy="afterInteractive"
          onLoad={initTurnstile}
        />
      )}
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">Platform Admin Login</CardTitle>
          <CardDescription>
            Sign in with your platform administrator account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="Enter your email"
                        {...field}
                        disabled={isLoading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Enter your password"
                          {...field}
                          disabled={isLoading}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                          onClick={() => setShowPassword(!showPassword)}
                          disabled={isLoading}
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Turnstile invisible widget container */}
              <div id="turnstile-container" />

              {error && (
                <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
                  {error}
                </div>
              )}

              <Button type="submit" className="w-full" disabled={isLoading || !turnstileReady}>
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Signing in...
                  </div>
                ) : !turnstileReady ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
                    Loading...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <LogIn className="h-4 w-4" />
                    Sign In
                  </div>
                )}
              </Button>
            </form>
          </Form>

          <div className="mt-6 text-center text-sm text-gray-600">
            <p className="mb-2">Platform administrators only</p>
            <p className="text-xs text-muted-foreground">
              Tenant staff should use their tenant-specific login page
            </p>
          </div>

          <div className="mt-4 text-center text-sm text-gray-600">
            <p className="text-xs">Test credentials:</p>
            <p className="text-xs"><strong>Master Admin:</strong> master.admin@nomail.com / 1234</p>
            <p className="text-xs"><strong>Super Admin:</strong> casino.admin@nomail.com / 1234</p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default function LoginPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex items-center justify-center p-8">
            <div className="w-6 h-6 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
          </CardContent>
        </Card>
      </div>
    }>
      <LoginForm />
    </Suspense>
  )
}
